// Etsy Profit Calculator — shared admin + seller tool.
//
// Per Volkan ratify 2026-05-31: sellers compute their OWN profit from their
// OWN cost inputs to decide a sell price. No PEXIFY supplier/producer data is
// pre-filled, so no D-6 / seller-surface supplier leak. Same component mounts
// under /admin/tools/etsy-profit and /seller/tools/etsy-profit.
//
// Pure math lives in lib/etsyProfit.ts (framework-independent, tested).
//
// Wave-5 Day 1 i18n: chrome bilingual via ETSY_PROFIT_STRINGS dict
// (audit "largest tool deep-dive ~95 string" closure). TRY/USD output use
// formatCurrency (currency-bound, not user-locale-aware — USD always
// $1,234.56, TRY always 1.234,56 ₺ per Wave-1 ratify).

import { useMemo, useState } from 'react'
import { Calculator, AlertTriangle, TrendingUp, Receipt, Wallet, DollarSign } from 'lucide-react'
import {
  calcEtsyProfit,
  type CountryCode,
  type OffsiteAdsRate,
  type EtsyProfitInput,
} from '../../lib/etsyProfit'
import { Card } from '../../components/ui/card'
import { Badge } from '../../components/ui/badge'
import { useT } from '../../i18n'
import { ETSY_PROFIT_STRINGS } from '../../i18n/tools/etsy-profit'
import { formatCurrency } from '../../lib/currency'

function formatUsd(v: number): string {
  return formatCurrency(Number.isFinite(v) ? v : 0, 'USD')
}

function formatTry(v: number): string {
  return formatCurrency(Number.isFinite(v) ? v : 0, 'TRY')
}

type FormState = {
  countryCode: CountryCode
  usdToLocalRate: string
  itemPrice: string
  quantity: string
  shippingCharged: string
  giftWrapCharged: string
  unitCost: string
  shippingCost: string
  packagingCost: string
  adCostPerOrder: string
  offsiteAdsRate: OffsiteAdsRate
  currencyConversionEnabled: boolean
  applyDepositFee: boolean
  sellerFeeTaxPercent: string
}

const INITIAL: FormState = {
  countryCode: 'US',
  usdToLocalRate: '',
  itemPrice: '',
  quantity: '1',
  shippingCharged: '',
  giftWrapCharged: '',
  unitCost: '',
  shippingCost: '',
  packagingCost: '',
  adCostPerOrder: '',
  offsiteAdsRate: 0,
  currencyConversionEnabled: false,
  applyDepositFee: false,
  sellerFeeTaxPercent: '',
}

function num(s: string): number {
  const v = parseFloat(s)
  return Number.isFinite(v) ? v : 0
}

function NumberField({
  label,
  value,
  onChange,
  prefix,
  suffix,
  step = '0.01',
}: {
  label: string
  value: string
  onChange: (v: string) => void
  prefix?: string
  suffix?: string
  step?: string
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-slate-600 appearance-dark:text-[var(--color-text-muted)]">{label}</span>
      <div className="flex items-center rounded-md border border-slate-300 appearance-dark:border-[var(--color-border)] bg-white appearance-dark:bg-[var(--color-surface)] focus-within:ring-2 focus-within:ring-slate-400">
        {prefix && <span className="pl-2.5 text-sm text-slate-400 appearance-dark:text-[var(--color-text-muted)]">{prefix}</span>}
        <input
          type="number"
          inputMode="decimal"
          step={step}
          min="0"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent px-2.5 py-1.5 text-sm text-slate-900 appearance-dark:text-[var(--color-text)] outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
          placeholder="0"
        />
        {suffix && <span className="pr-2.5 text-sm text-slate-400 appearance-dark:text-[var(--color-text-muted)]">{suffix}</span>}
      </div>
    </label>
  )
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-3 rounded-md border border-slate-200 appearance-dark:border-[var(--color-border)] px-3 py-2">
      <span className="text-xs font-medium text-slate-700 appearance-dark:text-[var(--color-text)]">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${
          checked ? 'bg-emerald-500' : 'bg-slate-300 appearance-dark:bg-[var(--color-surface-muted)]'
        }`}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white appearance-dark:bg-[var(--color-surface)] transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </button>
    </label>
  )
}

export function EtsyProfitCalculator({ role }: { role: 'admin' | 'seller' }) {
  const t = useT(ETSY_PROFIT_STRINGS)
  const [form, setForm] = useState<FormState>(INITIAL)
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  const rate = num(form.usdToLocalRate)
  const isTR = form.countryCode === 'TR'
  const showTry = isTR && rate > 0

  const result = useMemo(() => {
    const input: EtsyProfitInput = {
      countryCode: form.countryCode,
      usdToLocalRate: rate,
      itemPrice: num(form.itemPrice),
      quantity: num(form.quantity) || 1,
      shippingCharged: num(form.shippingCharged),
      giftWrapCharged: num(form.giftWrapCharged),
      unitCost: num(form.unitCost),
      shippingCost: num(form.shippingCost),
      packagingCost: num(form.packagingCost),
      adCostPerOrder: num(form.adCostPerOrder),
      offsiteAdsRate: form.offsiteAdsRate,
      currencyConversionEnabled: form.currencyConversionEnabled,
      applyDepositFee: form.applyDepositFee,
      sellerFeeTaxPercent: num(form.sellerFeeTaxPercent),
    }
    return calcEtsyProfit(input)
  }, [form, rate])

  const money = (v: number) =>
    showTry ? `${formatUsd(v)}  ·  ${formatTry(v * rate)}` : formatUsd(v)

  const profitPositive = result.netProfit >= 0

  const breakdownLabels: Record<string, string> = {
    listingFee: t.breakdownLabels.listingFee,
    transaction: t.breakdownLabels.transaction,
    payment: t.breakdownLabels.payment,
    regulatory: t.breakdownLabels.regulatory,
    offsiteAds: t.breakdownLabels.offsiteAds,
    currencyConv: t.breakdownLabels.currencyConv,
    depositFee: t.breakdownLabels.depositFee,
    tax: t.breakdownLabels.tax,
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900 appearance-dark:text-[var(--color-text)]">
          <Calculator size={22} className="text-indigo-600 appearance-dark:text-[var(--color-info)]" aria-hidden="true" />
          {t.pageTitle}
        </h1>
        <p className="mt-1 text-xs text-slate-500 appearance-dark:text-[var(--color-text-muted)]">
          {t.subtitle.common}
          {role === 'seller'
            ? ` · ${t.subtitle.sellerSuffix}`
            : ` · ${t.subtitle.adminSuffix}`}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        {/* ── Inputs ──────────────────────────────────────────── */}
        <Card className="p-5">
          <div className="space-y-5">
            {/* Country + rate */}
            <section className="space-y-3">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-400 appearance-dark:text-[var(--color-text-muted)]">
                {t.inputSections.country.title}
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-slate-600 appearance-dark:text-[var(--color-text-muted)]">{t.inputSections.country.countryLabel}</span>
                  <select
                    value={form.countryCode}
                    onChange={(e) => set('countryCode', e.target.value as CountryCode)}
                    className="rounded-md border border-slate-300 appearance-dark:border-[var(--color-border)] bg-white appearance-dark:bg-[var(--color-surface)] px-2.5 py-1.5 text-sm text-slate-900 appearance-dark:text-[var(--color-text)] outline-none focus:ring-2 focus:ring-slate-400 appearance-dark:focus:ring-[var(--color-border)]"
                  >
                    <option value="US">{t.inputSections.country.countryOptions.us}</option>
                    <option value="TR">{t.inputSections.country.countryOptions.tr}</option>
                  </select>
                </label>
                {isTR && (
                  <NumberField
                    label={t.inputSections.country.usdToTryLabel}
                    value={form.usdToLocalRate}
                    onChange={(v) => set('usdToLocalRate', v)}
                    suffix="₺"
                    step="0.0001"
                  />
                )}
              </div>
            </section>

            {/* Revenue */}
            <section className="space-y-3">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-400 appearance-dark:text-[var(--color-text-muted)]">
                {t.inputSections.revenue.title}
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <NumberField label={t.inputSections.revenue.itemPriceLabel} value={form.itemPrice} onChange={(v) => set('itemPrice', v)} prefix="$" />
                <NumberField label={t.inputSections.revenue.quantityLabel} value={form.quantity} onChange={(v) => set('quantity', v)} step="1" />
                <NumberField label={t.inputSections.revenue.shippingChargedLabel} value={form.shippingCharged} onChange={(v) => set('shippingCharged', v)} prefix="$" />
                <NumberField label={t.inputSections.revenue.giftWrapChargedLabel} value={form.giftWrapCharged} onChange={(v) => set('giftWrapCharged', v)} prefix="$" />
              </div>
            </section>

            {/* Operating cost */}
            <section className="space-y-3">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-400 appearance-dark:text-[var(--color-text-muted)]">
                {t.inputSections.costs.title}
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <NumberField label={t.inputSections.costs.unitCostLabel} value={form.unitCost} onChange={(v) => set('unitCost', v)} prefix="$" />
                <NumberField label={t.inputSections.costs.shippingCostLabel} value={form.shippingCost} onChange={(v) => set('shippingCost', v)} prefix="$" />
                <NumberField label={t.inputSections.costs.packagingCostLabel} value={form.packagingCost} onChange={(v) => set('packagingCost', v)} prefix="$" />
                <NumberField label={t.inputSections.costs.adCostPerOrderLabel} value={form.adCostPerOrder} onChange={(v) => set('adCostPerOrder', v)} prefix="$" />
              </div>
            </section>

            {/* Etsy settings */}
            <section className="space-y-3">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-400 appearance-dark:text-[var(--color-text-muted)]">
                {t.inputSections.etsySettings.title}
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-slate-600 appearance-dark:text-[var(--color-text-muted)]">{t.inputSections.etsySettings.offsiteAdsLabel}</span>
                  <select
                    value={String(form.offsiteAdsRate)}
                    onChange={(e) => set('offsiteAdsRate', Number(e.target.value) as OffsiteAdsRate)}
                    className="rounded-md border border-slate-300 appearance-dark:border-[var(--color-border)] bg-white appearance-dark:bg-[var(--color-surface)] px-2.5 py-1.5 text-sm text-slate-900 appearance-dark:text-[var(--color-text)] outline-none focus:ring-2 focus:ring-slate-400 appearance-dark:focus:ring-[var(--color-border)]"
                  >
                    <option value="0">{t.inputSections.etsySettings.offsiteAdsOptions.none}</option>
                    <option value="0.12">{t.inputSections.etsySettings.offsiteAdsOptions.twelve}</option>
                    <option value="0.15">{t.inputSections.etsySettings.offsiteAdsOptions.fifteen}</option>
                  </select>
                </label>
                <NumberField label={t.inputSections.etsySettings.sellerFeeTaxLabel} value={form.sellerFeeTaxPercent} onChange={(v) => set('sellerFeeTaxPercent', v)} suffix="%" />
              </div>
              <div className="grid grid-cols-1 gap-2">
                <Toggle
                  label={t.inputSections.etsySettings.currencyConversionLabel}
                  checked={form.currencyConversionEnabled}
                  onChange={(v) => set('currencyConversionEnabled', v)}
                />
                {isTR && (
                  <Toggle
                    label={t.inputSections.etsySettings.depositFeeLabel}
                    checked={form.applyDepositFee}
                    onChange={(v) => set('applyDepositFee', v)}
                  />
                )}
              </div>
            </section>
          </div>
        </Card>

        {/* ── Results ─────────────────────────────────────────── */}
        <div className="space-y-4">
          {result.rateWarning && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 appearance-dark:border-[var(--color-border)] bg-amber-50 appearance-dark:bg-[var(--color-surface-muted)] px-3 py-2 text-xs text-amber-800 appearance-dark:text-[var(--color-warning)]">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                {t.warnings.rateWarningPrefix}{' '}
                <strong>{t.warnings.rateWarningKurLabel}</strong>
                {t.warnings.rateWarningSuffix}
              </span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <ResultCard label={t.resultCards.grossRevenue} value={money(result.grossRevenue)} icon={<DollarSign size={16} />} tone="neutral" />
            <ResultCard label={t.resultCards.totalEtsyFees} value={money(result.totalEtsyFees)} icon={<Receipt size={16} />} tone="warning" />
            <ResultCard label={t.resultCards.operatingCost} value={money(result.operatingCost)} icon={<Wallet size={16} />} tone="neutral" />
            <ResultCard
              label={t.resultCards.netProfit}
              value={money(result.netProfit)}
              icon={<TrendingUp size={16} />}
              tone={profitPositive ? 'success' : 'destructive'}
              badge={t.resultCards.marginBadge(result.profitMargin.toFixed(1))}
            />
          </div>

          {/* Breakdown */}
          <Card className="p-4">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400 appearance-dark:text-[var(--color-text-muted)]">
              {t.breakdownLabels.sectionTitle}
            </h2>
            <ul className="divide-y divide-slate-100 appearance-dark:divide-[var(--color-border)] text-sm">
              {Object.entries(result.breakdown).map(([key, val]) => {
                if (val === 0 && key !== 'tax') return null
                return (
                  <li key={key} className="flex items-center justify-between py-1.5">
                    <span className="text-slate-600 appearance-dark:text-[var(--color-text-muted)]">{breakdownLabels[key] ?? key}</span>
                    <span className="font-medium text-slate-900 appearance-dark:text-[var(--color-text)]">{formatUsd(val)}</span>
                  </li>
                )
              })}
              <li className="flex items-center justify-between border-t-2 border-slate-200 appearance-dark:border-[var(--color-border)] py-1.5">
                <span className="font-semibold text-slate-700 appearance-dark:text-[var(--color-text)]">{t.breakdownLabels.totalRow}</span>
                <span className="font-semibold text-slate-900 appearance-dark:text-[var(--color-text)]">{formatUsd(result.totalEtsyFees)}</span>
              </li>
            </ul>
          </Card>

          {/* Monthly projection */}
          <Card className="p-4">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400 appearance-dark:text-[var(--color-text-muted)]">
              {t.monthly.sectionTitle}
            </h2>
            <div className="grid grid-cols-4 gap-2">
              {result.monthly.map((m) => (
                <div key={m.perDay} className="rounded-md bg-slate-50 appearance-dark:bg-[var(--color-surface-muted)] px-2 py-2 text-center">
                  <div className="text-[11px] text-slate-500 appearance-dark:text-[var(--color-text-muted)]">{m.perDay}{t.monthly.perDaySuffix}</div>
                  <div className={`mt-0.5 text-sm font-semibold ${m.amount >= 0 ? 'text-emerald-700 appearance-dark:text-[var(--color-success)]' : 'text-rose-600 appearance-dark:text-[var(--color-danger)]'}`}>
                    {formatUsd(m.amount)}
                  </div>
                  {showTry && (
                    <div className="text-[10px] text-slate-400 appearance-dark:text-[var(--color-text-muted)]">{formatTry(m.amount * rate)}</div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}

function ResultCard({
  label,
  value,
  icon,
  tone,
  badge,
}: {
  label: string
  value: string
  icon: React.ReactNode
  tone: 'neutral' | 'warning' | 'success' | 'destructive'
  badge?: string
}) {
  const toneRing: Record<string, string> = {
    neutral: 'border-slate-200 appearance-dark:border-[var(--color-border)]',
    warning: 'border-amber-200 appearance-dark:border-[var(--color-border)]',
    success: 'border-emerald-200 appearance-dark:border-[var(--color-border)]',
    destructive: 'border-rose-200 appearance-dark:border-[var(--color-border)]',
  }
  const toneText: Record<string, string> = {
    neutral: 'text-slate-900 appearance-dark:text-[var(--color-text)]',
    warning: 'text-amber-700 appearance-dark:text-[var(--color-warning)]',
    success: 'text-emerald-700 appearance-dark:text-[var(--color-success)]',
    destructive: 'text-rose-600 appearance-dark:text-[var(--color-danger)]',
  }
  const badgeVariant = tone === 'destructive' ? 'destructive' : 'success'
  return (
    <Card className={`p-4 ${toneRing[tone]}`}>
      <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 appearance-dark:text-[var(--color-text-muted)]">
        <span className="text-slate-400 appearance-dark:text-[var(--color-text-muted)]">{icon}</span>
        {label}
      </div>
      <div className={`mt-1.5 text-lg font-semibold tabular-nums ${toneText[tone]}`}>{value}</div>
      {badge && (
        <Badge variant={badgeVariant} className="mt-1.5">
          {badge}
        </Badge>
      )}
    </Card>
  )
}
